import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Button, IconButton } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    ConfirmModal,
    CustomAttributeType,
    ICustomAttributeConfiguration,
    Page,
    SortOrder,
    Table,
    useQueryParamsContext,
    useSelectedOrganisation,
} from '../../../shared';
import { SearchInput } from '../../../shared/components/search/search.component';
import {
    useCustomAttributeConfigurationList,
    useDeleteCustomAttributeConfiguration,
} from '../../hooks/custom-attributes-configuration.hook';
import { Edit } from '@mui/icons-material';

export const CustomAttributeConfigurationsPage: FC = () => {
    const { t } = useTranslation();
    const { organisation } = useSelectedOrganisation();
    const {
        queryParams: { page, pageSize, sort, search },
        debouncedQueryParams,
        setPage,
        setPageSize,
        setSort,
        setSearch,
    } = useQueryParamsContext('custom-attribute-configurations', {
        sort: [{ field: 'name', sort: 'asc' }],
    });

    const { isLoading, data: customAttributeConfigurations } = useCustomAttributeConfigurationList(
        {
            page,
            pageSize,
            sortBy: sort[0].field,
            sortOrder: sort[0].sort?.toUpperCase() as SortOrder,
            search: debouncedQueryParams.search,
            organisationId: organisation?.id,
        },
        { enabled: !!organisation?.id && (pageSize == undefined || pageSize > 0) },
    );
    const { mutateAsync: deleteCustomAttributeConfiguration } = useDeleteCustomAttributeConfiguration();

    const columns: GridColDef[] = [
        { field: 'name', headerName: t('nameLabel'), minWidth: 150, flex: 1 },
        { field: 'code', headerName: t('codeLabel'), minWidth: 150, flex: 0.5 },
        {
            field: 'type',
            headerName: t('typeLabel'),
            minWidth: 150,
            flex: 0.5,
            valueFormatter: (value: CustomAttributeType) => t(value),
        },
        {
            field: 'defaultSelected',
            headerName: t('defaultSelectedLabel'),
            flex: 1,
            valueFormatter: (value: boolean) => (value ? t('yes') : t('no')),
        },
        {
            field: 'actions',
            headerName: t('actions'),
            width: 100,
            align: 'right',
            sortable: false,
            renderCell: ({ row: { id } }: GridCellParams<ICustomAttributeConfiguration>) => (
                <>
                    <IconButton component={Link} to={`/admin/custom-attribute-configurations/edit/${id}`}>
                        <Edit />
                    </IconButton>
                    <ConfirmModal
                        onConfirm={() =>
                            deleteCustomAttributeConfiguration({
                                id,
                                messages: {
                                    onDeleteSuccessMessage: t('deleteCustomAttributeConfigurationSuccess'),
                                    onDeleteErrorMessage: t('deleteCustomAttributeConfigurationError'),
                                },
                            })
                        }
                        button={
                            <IconButton aria-label="delete">
                                <DeleteOutlineIcon />
                            </IconButton>
                        }
                        title={t('customAttributeConfigurationDeleteWarningTitle')}
                        text={t('customAttributeConfigurationDeleteWarningText')}
                    />
                </>
            ),
        },
    ];

    return (
        <Page
            title={t('customAttributeConfigurations')}
            actions={[
                <SearchInput value={search} setValue={setSearch} />,
                <Button
                    component={Link}
                    to="/admin/custom-attribute-configurations/edit"
                    color="primary"
                    variant="contained"
                    data-testid="newCustomAttributeConfiguration"
                >
                    {t('newCustomAttributeConfiguration')}
                </Button>,
            ]}
        >
            <Table
                rows={customAttributeConfigurations?.data || []}
                columns={columns}
                rowCount={customAttributeConfigurations?.pagination?.size || 0}
                page={page}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
                loading={isLoading || !organisation}
                sortModel={sort}
                onSortModelChange={setSort}
            />
        </Page>
    );
};
